import { useState, useRef, useEffect } from "react";
import { useSnapshot } from "valtio/react";
import { mapStore } from "src/js/stores/map/store";

type UseDebouncedTooltipInfoOptions = {
  tooltipHovered: boolean;
  showDelay: number;
  hideDelay: number;
};

/**
 * Used to control the showing/hiding of a tooltip
 */
export function useDebouncedTooltipInfo<C>(
  tooltipInfo: C | null,
  { tooltipHovered, showDelay, hideDelay }: UseDebouncedTooltipInfoOptions
): C | null {
  const { googleMap } = useSnapshot(mapStore);
  const [debouncedInfo, setDebouncedInfo] = useState<C | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(false);

  useEffect(
    function debounceShowHideOfTooltip() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (tooltipInfo !== null) {
        if (isVisibleRef.current) {
          // Tooltip already visible - show new one immediately
          setDebouncedInfo(tooltipInfo);
          isVisibleRef.current = true;
        } else {
          // Tooltip not visible - wait for showDelay
          timerRef.current = setTimeout(() => {
            setDebouncedInfo(tooltipInfo);
            isVisibleRef.current = true;
            timerRef.current = null;
          }, showDelay);
        }
      } else if (!tooltipHovered) {
        // Tooltip info is null and not hovered - wait for hideDelay
        timerRef.current = setTimeout(() => {
          setDebouncedInfo(null);
          isVisibleRef.current = false;
          timerRef.current = null;
        }, hideDelay);
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    },
    [tooltipInfo, tooltipHovered, showDelay, hideDelay]
  );

  useEffect(
    function hideTooltipOnMapInteraction() {
      if (!googleMap) return;

      const hideTooltip = () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setDebouncedInfo(null);
        isVisibleRef.current = false;
      };

      const zoomListener = google.maps.event.addListener(googleMap, "zoom_changed", hideTooltip);
      const boundsListener = google.maps.event.addListener(googleMap, "bounds_changed", hideTooltip);

      return () => {
        google.maps.event.removeListener(zoomListener);
        google.maps.event.removeListener(boundsListener);
      };
    },
    [googleMap]
  );

  isVisibleRef.current = debouncedInfo !== null;

  return debouncedInfo;
}
